// ============================================================================
// AIProjectCard — Expandable theme card with sub-collection tree
// Uses collection media images from Redux for thumbnails
// ============================================================================

import React, { memo, useCallback } from 'react';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { useAppSelector } from '../../redux/hooks';
import type { Collection } from '../../types/collections.types';

interface AIProjectCardProps {
  themeName: string;
  collections: Collection[];
  totalProducts: number;
  isExpanded: boolean;
  selectedCollectionId: string | null;
  onToggleExpand: (themeName: string) => void;
  onSelectCollection: (pkId: string) => void;
}

/** Get the best thumbnail for a collection: media image → fallback to collection.image */
const useCollectionThumbnail = (pkId: string, fallback: string): string => {
  const mediaUrl = useAppSelector(
    (s) => s.collections.collectionMedia[pkId]?.image_url
  );
  const mediaStatus = useAppSelector(
    (s) => s.collections.collectionMedia[pkId]?.image_status
  );
  return mediaStatus === 'completed' && mediaUrl ? mediaUrl : fallback;
};

// Sub-collection item (needs hook, so separate component)
const SubCollectionItem: React.FC<{
  col: Collection;
  isSelected: boolean;
  onSelect: (pkId: string) => void;
}> = memo(({ col, isSelected, onSelect }) => {
  const thumb = useCollectionThumbnail(col.pk_id, col.image || '');

  return (
    <button
      onClick={() => onSelect(col.pk_id)}
      className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-all duration-150 cursor-pointer relative ${
        isSelected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
      }`}
    >
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[#EE4423]" />
      )}
      {/* Mini thumbnail */}
      {thumb ? (
        <img src={thumb} alt={col.title} className="w-7 h-7 rounded object-cover bg-white flex-shrink-0" loading="lazy" />
      ) : (
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isSelected ? 'bg-[#EE4423]' : 'bg-gray-600'}`} />
      )}
      <span className={`text-[12px] line-clamp-1 ${isSelected ? 'text-white font-medium' : 'text-gray-400'}`}>
        {col.title}
      </span>
    </button>
  );
});
SubCollectionItem.displayName = 'SubCollectionItem';

// Theme card header thumbnail
const ThemeThumbnail: React.FC<{ pkId: string; fallback: string; alt: string }> = memo(({ pkId, fallback, alt }) => {
  const thumb = useCollectionThumbnail(pkId, fallback);
  return (
    <img
      src={thumb || 'https://via.placeholder.com/40'}
      alt={alt}
      className="w-10 h-10 rounded-lg object-cover bg-gray-800 flex-shrink-0"
      loading="lazy"
    />
  );
});
ThemeThumbnail.displayName = 'ThemeThumbnail';

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
const AIProjectCard: React.FC<AIProjectCardProps> = memo(({
  themeName,
  collections,
  totalProducts,
  isExpanded,
  selectedCollectionId,
  onToggleExpand,
  onSelectCollection,
}) => {
  const handleToggle = useCallback(() => onToggleExpand(themeName), [themeName, onToggleExpand]);
  const firstCol = collections[0];

  return (
    <div className="mb-1">
      {/* Theme header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150 hover:bg-white/[0.04] cursor-pointer group"
      >
        {/* <ThemeThumbnail pkId={firstCol?.pk_id || ''} fallback={firstCol?.image || ''} alt={themeName} /> */}

        <div className="flex-1 min-w-0 text-left">
          <h4 className="text-[13px] font-semibold text-white line-clamp-1 leading-tight">
            {themeName}
          </h4>
          <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
            {collections.length} Collection{collections.length !== 1 ? 's' : ''}
          </p>
          <p className="text-[11px] text-gray-500 leading-tight">
            {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex-shrink-0 text-gray-500 group-hover:text-gray-300 transition-colors">
          {isExpanded ? <MdExpandMore size={18} /> : <MdChevronRight size={18} />}
        </div>
      </button>

      {/* Sub-collections tree */}
      {isExpanded && (
        <div className="ml-5 pl-4 border-l border-white/[0.06]">
          {collections.map((col) => (
            <SubCollectionItem
              key={col.pk_id}
              col={col}
              isSelected={selectedCollectionId === col.pk_id}
              onSelect={onSelectCollection}
            />
          ))}
        </div>
      )}
    </div>
  );
});

AIProjectCard.displayName = 'AIProjectCard';
export default AIProjectCard;
